// @flow

import classNames from 'classnames';
import gql from 'graphql-tag';
import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import ScreenReaderDetails from '../ScreenReaderDetails/ScreenReaderDetails';

import './Details.less';

import type {Node} from 'react';

export const fragments = {
    Listing: gql`
        fragment DetailsListing on Listing {
            bathrooms {
                full
                half
                toiletOnly
            }
            bedrooms
            propertyType
            sleeps
            petsAllowed
            spaces {
                spacesSummary {
                    area {
                        areaValue
                    }
                    bedCountDisplay
                }
            }
        }
    `
};

export type ListingShape = {
    bathrooms?: {
        full?: number,
        half?: number,
        toiletOnly?: number
    },
    bedrooms: number,
    propertyType: string,
    sleeps: number,
    petsAllowed: boolean,
    spaces: {
        spacesSummary: {area?: {areaValue?: number}, bedCountDisplay?: string}
    },
    address?: {
        city: string,
        stateProvince: string
    }
};

export type Props = {
    listing: ListingShape,
    isMetric: boolean,
    shouldHideArea?: boolean,
    locale?: string
};

export type AreaElementType = {
    area: number,
    isMetric?: boolean
};

export type BedroomsPropertyTypeElementType = {
    bedrooms: number,
    propertyType: string
};

export type DetailsItemShape = {
    id: string,
    classname: string,
    value: number
};

export type HalfBathElementType = {
    bathroomsHalf?: number,
    bathroomsToiletOnly?: number
};

export type BathroomsElementType = {
    bathroomsFull?: number,
    bathroomsHalf?: number,
    bathroomsToiletOnly?: number
};

export type SleepsElementShape = {
    value: number
};

export function DetailsItem({id, classname, value}: DetailsItemShape) {
    if (value && value > 0) {
        return (
            <div
                className={classNames(
                    `Details__${classname}`,
                    'Details__label'
                )}
                aria-hidden="true"
            >
                <FormattedMessage
                    id={id}
                    description={`${classname} label`}
                    defaultMessage={`${classname}`}
                >
                    {(txt: Node): Node => (
                        <Fragment>
                            {value} {txt}
                        </Fragment>
                    )}
                </FormattedMessage>
            </div>
        );
    }
    return null;
}

export function BedroomsPropertyTypeElement({
    bedrooms,
    propertyType
}: BedroomsPropertyTypeElementType) {
    if (bedrooms > 0) {
        return (
            <Fragment>
                <div
                    className="Details__propertyType Details__label"
                    aria-hidden="true"
                >
                    {propertyType}
                </div>
                <div
                    className="Details__bedrooms Details__label"
                    aria-hidden="true"
                >
                    <FormattedMessage
                        id="bedroomsAbbreviationParam"
                        description="bedrooms abbreviation label"
                        defaultMessage="BR"
                        values={{
                            bedrooms
                        }}
                    >
                        {(txt: Node): Node => txt}
                    </FormattedMessage>
                </div>
            </Fragment>
        );
    }
    return (
        <Fragment>
            <div
                className="Details__propertyType Details__label"
                aria-hidden="true"
            >
                {propertyType}
            </div>
        </Fragment>
    );
}

export function HalfBathElement({
    bathroomsHalf,
    bathroomsToiletOnly
}: HalfBathElementType) {
    if (bathroomsHalf && bathroomsHalf > 0) {
        return (
            <DetailsItem
                id="bathroomsHalfAbbreviation"
                classname="halfbathrooms"
                value={bathroomsHalf}
            />
        );
    } else if (bathroomsToiletOnly && bathroomsToiletOnly > 0) {
        return (
            <DetailsItem
                id="bathroomsToiletOnlyAbbreviation"
                classname="halfbathrooms"
                value={bathroomsToiletOnly}
            />
        );
    }
    return null;
}

export function SleepsElement({value}: SleepsElementShape) {
    if (value && value > 0) {
        return (
            <div className="Details__sleeps Details__label" aria-hidden="true">
                <FormattedMessage
                    id="sleepsAbbreviation"
                    description="sleeps label"
                    defaultMessage="Sleeps {value}"
                    values={{
                        value
                    }}
                >
                    {(txt: Node): Node => txt}
                </FormattedMessage>
            </div>
        );
    }
    return null;
}

export function BathroomsElement({
    bathroomsFull,
    bathroomsHalf,
    bathroomsToiletOnly
}: BathroomsElementType) {
    if (bathroomsFull && bathroomsFull > 0) {
        return (
            <Fragment>
                <div
                    className="Details__bathrooms Details__label"
                    aria-hidden="true"
                >
                    <FormattedMessage
                        id="bathroomsFullAbbreviationParam"
                        description={`bathrooms label`}
                        defaultMessage={`bathrooms`}
                        values={{
                            bathroomsFull
                        }}
                    >
                        {(txt: Node): Node => txt}
                    </FormattedMessage>
                </div>
                <HalfBathElement
                    bathroomsHalf={bathroomsHalf}
                    bathroomsToiletOnly={bathroomsToiletOnly}
                />
            </Fragment>
        );
    }
    return null;
}

export default function Details({
    listing: {bathrooms, bedrooms, propertyType, sleeps, spaces},
    isMetric,
    locale
}: Props) {
    const {
        half: bathroomsHalf,
        full: bathroomsFull,
        toiletOnly: bathroomsToiletOnly
    } = bathrooms || {};

    const areaValue =
        spaces &&
        spaces.spacesSummary &&
        spaces.spacesSummary.area &&
        spaces.spacesSummary.area.areaValue;
    return (
        <Fragment>
            <BedroomsPropertyTypeElement
                bedrooms={bedrooms}
                propertyType={propertyType}
            />
            <BathroomsElement
                bathroomsFull={bathroomsFull}
                bathroomsHalf={bathroomsHalf}
                bathroomsToiletOnly={bathroomsToiletOnly}
            />
            <SleepsElement value={sleeps} />
            {!!(areaValue && areaValue > 0) && (
                <div className="Details__label" aria-hidden="true">
                    {`${areaValue} `}
                    <FormattedMessage
                        id="areaLabel"
                        description="units for area"
                        defaultMessage={'Sq. Ft.'}
                        values={{
                            METRIC: isMetric,
                            sup: /* istanbul ignore next */ (...chunks) => (
                                <sup>{chunks}</sup>
                            )
                        }}
                    >
                        {/* istanbul ignore next */
                        (...formattedMessageText: Node): Node =>
                            formattedMessageText}
                    </FormattedMessage>
                </div>
            )}
            <ScreenReaderDetails
                bedrooms={bedrooms}
                propertyType={propertyType}
                bathroomsFull={bathroomsFull}
                bathroomsHalf={bathroomsHalf}
                bathroomsToiletOnly={bathroomsToiletOnly}
                sleeps={sleeps}
                area={areaValue}
                isMetric={isMetric}
                local={locale}
            />
        </Fragment>
    );
}
